import { useCallback } from 'react';
import {
  useLocation,
  useNavigate,
  type NavigateOptions,
  type To,
} from 'react-router-dom';

/**
 * Shared query-param preservation for in-app navigation (DP-3188 AC8).
 *
 * Every in-app nav target inherits the current query, so any param that scopes
 * the session — the record-access dimension param (DSCO-5543), a tab, a filter
 * — survives a module switch without each call site opting in. This subsumes
 * the two ad-hoc helpers DP-3188 deleted: one appended the whole current query
 * to an href on request, the other appended one special-cased dimension key.
 *
 * The merge is **blind**: no store is read and no individual key is
 * special-cased. The dimension param is preserved purely because it is
 * already in the current query. To drop a specific key on navigation, a
 * caller writes it empty on the destination (`?key=`), which wins under the
 * destination-wins rule below. The one structural exception is the
 * page-scoped namespace (DP-6873): `p.`-prefixed keys never carry over —
 * see {@link PAGE_SCOPED_PREFIX}.
 */

/**
 * Absolute targets — a scheme (`https:`, `mailto:`, `tel:`) or a
 * protocol-relative `//host` — leave the app, so merging an app query into
 * them would leak state to a third party. Carries forward the external-href
 * guard the deleted dimension-param helper applied by default.
 */
const EXTERNAL_HREF_PATTERN = /^([a-zA-Z][a-zA-Z\d+\-.]*:|\/\/)/;
const HASH_SEPARATOR = '#';
const QUERY_SEPARATOR = '?';

/** Anchor-only (`#section`) and external targets are handed on untouched. */
function isPassThroughHref(href: string): boolean {
  return href.startsWith(HASH_SEPARATOR) || EXTERNAL_HREF_PATTERN.test(href);
}

/**
 * Page-scoped params (DP-6873): a `p.`-prefixed key belongs to the PAGE that
 * wrote it — a view toggle, a panel, a local mode — and must not ride the
 * blind merge onto other destinations the way session-scoped keys (the
 * record-access dimension, `node`) deliberately do. One namespace rule, not a
 * key list: the dashboard still special-cases no app's key, it recognises the
 * prefix. A destination that names a `p.` key explicitly keeps it — linking
 * INTO a page state stays possible — and a page's own same-URL writes never
 * pass through this merge, so its state survives until the user leaves.
 */
const PAGE_SCOPED_PREFIX = 'p.';

/**
 * Current query as the base, destination query layered on top: the
 * destination wins on key collision (Q7), the current query only fills in keys
 * the destination does not mention. Page-scoped (`p.*`) keys never carry over
 * from the current query (see {@link PAGE_SCOPED_PREFIX}).
 */
function mergeSearch(currentSearch: string, destinationSearch: string): string {
  const merged = new URLSearchParams(currentSearch);
  for (const key of new Set(merged.keys())) {
    if (key.startsWith(PAGE_SCOPED_PREFIX)) {
      merged.delete(key);
    }
  }
  const destination = new URLSearchParams(destinationSearch);
  for (const key of new Set(destination.keys())) {
    // `set` overwrites in place, so a key present in both keeps its original
    // position; repeated destination values are re-appended after it.
    const [first, ...rest] = destination.getAll(key);
    merged.set(key, first);
    rest.forEach(value => merged.append(key, value));
  }
  return merged.toString();
}

/**
 * The one merge implementation behind both {@link useDashboardNavigate} and
 * `<DashboardLink>`. Exported for those two consumers (and their tests) — call
 * sites should use the hook or the component, not this.
 */
export function mergeCurrentSearchIntoTo(to: To, currentSearch: string): To {
  if (typeof to !== 'string') {
    const search = mergeSearch(currentSearch, to.search ?? '');
    return { ...to, search: search ? `${QUERY_SEPARATOR}${search}` : '' };
  }
  if (isPassThroughHref(to)) {
    return to;
  }

  const hashAt = to.indexOf(HASH_SEPARATOR);
  const withoutHash = hashAt === -1 ? to : to.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : to.slice(hashAt);

  const queryAt = withoutHash.indexOf(QUERY_SEPARATOR);
  const path = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const destinationSearch =
    queryAt === -1 ? '' : withoutHash.slice(queryAt + 1);

  const search = mergeSearch(currentSearch, destinationSearch);
  const searchPart = search ? `${QUERY_SEPARATOR}${search}` : '';
  return `${path}${searchPart}${hash}`;
}

/**
 * `useNavigate` with the current query merged into every in-app destination.
 *
 * A history **delta** (`navigate(-1)`) is forwarded untouched: it replays an
 * entry the browser already holds, complete with the query it was pushed with,
 * so there is nothing to merge into. Accepting it keeps this a drop-in
 * replacement for `useNavigate` — no call site needs to hold both hooks.
 *
 * @returns a `navigate` with react-router's signature — `NavigateOptions`
 *   (including `replace`) are forwarded verbatim for a `To` target.
 */
export function useDashboardNavigate() {
  const navigate = useNavigate();
  const { search } = useLocation();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      navigate(mergeCurrentSearchIntoTo(to, search), options);
    },
    [navigate, search]
  );
}

/**
 * The same merge for call sites that must hand a plain **href string** to a
 * component that renders the anchor itself — `PageHeader`'s `BreadcrumbItem`
 * takes `href?: string` with no render slot, so `<DashboardLink>` cannot be
 * substituted there.
 *
 * Prefer {@link useDashboardNavigate} or `<DashboardLink>`; reach for this only
 * when the anchor is out of the call site's hands.
 *
 * @returns a mapper from an in-app path to that path with the current query
 *   merged in, under the same rules as the hook and the component.
 */
export function useDashboardHref() {
  const { search } = useLocation();

  return useCallback(
    (href: string) => mergeCurrentSearchIntoTo(href, search) as string,
    [search]
  );
}
